import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer, Area, AreaChart, ComposedChart
} from "recharts";
import { UploadCloud, TrendingUp, TrendingDown, Users, Clock, Activity, DollarSign, UserPlus, RefreshCw, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = ["", "Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const MONTH_FULL = ["", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const fmt = (v) => v != null ? new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v) : "—";
const fmtDec = (v) => v != null ? new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) : "—";
const fmtNum = (v) => v != null ? new Intl.NumberFormat("it-IT").format(v) : "—";

export const ClinicKpiDashboard = ({ clientId }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedYear, setSelectedYear] = useState("all");

  const fetchData = useCallback(async () => {
    try {
      const params = selectedYear !== "all" ? { year: selectedYear } : {};
      const res = await axios.get(`${API}/clients/${clientId}/clinic-kpis`, { params });
      setData(res.data);
    } catch (e) {
      if (e.response?.status !== 404) toast.error("Errore nel caricamento KPI operativi");
    } finally {
      setLoading(false);
    }
  }, [clientId, selectedYear]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/clients/${clientId}/clinic-kpis/upload`, formData);
      toast.success(`Importati ${res.data.count} mesi: ${res.data.imported_months.join(", ")}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const yearParam = selectedYear === "all" ? new Date().getFullYear() : selectedYear;
      const res = await axios.get(`${API}/clients/${clientId}/clinic-kpis/template`, {
        params: { year: yearParam },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const disp = res.headers["content-disposition"] || "";
      const match = disp.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : `Template_KPI_${yearParam}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template scaricato! Compila le celle gialle e ricaricalo");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore download template");
    }
  };

  if (loading) return <div className="py-12 text-center text-muted-foreground">Caricamento dati operativi...</div>;
  if (!data.length) return (
    <Card>
      <CardContent className="py-12 text-center">
        <Activity className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">Nessun dato operativo</h3>
        <p className="text-muted-foreground mb-1">Inizia scaricando il template, fallo compilare al cliente e poi caricalo qui.</p>
        <p className="text-xs text-muted-foreground mb-5">Il template è un file Excel con 1 foglio per ogni mese: bastano 5 minuti al mese per compilarlo.</p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <Button variant="outline" onClick={handleDownloadTemplate} data-testid="clinic-kpi-download-template-btn-empty">
            <Download className="h-4 w-4 mr-2" /> Scarica Template
          </Button>
          <label>
            <Button asChild data-testid="clinic-kpi-upload-btn-empty"><span><UploadCloud className="h-4 w-4 mr-2" /> Carica Excel compilato</span></Button>
            <input type="file" accept=".xlsx,.xls" onChange={handleUpload} className="hidden" />
          </label>
        </div>
      </CardContent>
    </Card>
  );

  // Compute aggregates
  const latest = data[data.length - 1];
  const prev = data.length > 1 ? data[data.length - 2] : null;
  const totFatturato = data.reduce((s, d) => s + d.fatturato_totale, 0);
  const totTrattamenti = data.reduce((s, d) => s + d.num_trattamenti, 0);
  const totCostiCollab = data.reduce((s, d) => s + (d.costi_collaboratori || 0), 0);
  const avgCostoPerTratt = totTrattamenti > 0 && totCostiCollab > 0 ? totCostiCollab / totTrattamenti : null;
  const avgRicavoPerTratt = totTrattamenti > 0 ? totFatturato / totTrattamenti : null;
  const avgMarginePerTratt = avgRicavoPerTratt != null && avgCostoPerTratt != null ? avgRicavoPerTratt - avgCostoPerTratt : null;

  // Trend for charts
  const chartData = data.map(d => ({
    label: `${MONTH_NAMES[d.month]} ${String(d.year).slice(2)}`,
    fatturato_titolare: d.fatturato_titolare,
    fatturato_collaboratori: d.fatturato_collaboratori,
    fatturato_totale: d.fatturato_totale,
    num_trattamenti: d.num_trattamenti,
    costo_per_trattamento: d.costo_per_trattamento,
    ricavo_per_trattamento: d.ricavo_per_trattamento,
    margine_per_trattamento: d.margine_per_trattamento,
    ore_collaboratori: d.ore_collaboratori,
    ore_apertura: d.ore_apertura,
    clienti_nuovi: d.clienti_nuovi,
    ticket_medio: d.ticket_medio,
    costi_collaboratori: d.costi_collaboratori || 0,
    pct_collab: d.fatturato_totale > 0 ? Math.round(d.fatturato_collaboratori / d.fatturato_totale * 100) : 0,
    // Indicatori di produttività
    tratt_per_ora_apertura: d.tratt_per_ora_apertura,
    tratt_per_ora_collab: d.tratt_per_ora_collab,
    fatt_per_ora_apertura: d.fatt_per_ora_apertura,
    fatt_per_ora_collab: d.fatt_per_ora_collab,
    pct_utilizzo_studio: d.pct_utilizzo_studio,
  }));

  const years = [...new Set(data.map(d => d.year))];

  const delta = (curr, previous, key) => {
    if (!previous) return null;
    const c = curr?.[key], p = previous?.[key];
    if (p == null || p === 0 || c == null) return null;
    return ((c - p) / Math.abs(p)) * 100;
  };

  // Build smart narrative for the latest month — each item has a `key` to bind it under the matching chart
  const buildNarrative = () => {
    if (!latest) return null;
    const items = [];

    // fatturato — bound to "Trend Fatturato"
    if (prev && prev.fatturato_totale > 0) {
      const dPct = ((latest.fatturato_totale - prev.fatturato_totale) / prev.fatturato_totale) * 100;
      const dAbs = latest.fatturato_totale - prev.fatturato_totale;
      const tone = dPct >= 5 ? 'good' : dPct <= -5 ? 'bad' : 'neutral';
      const verb = dPct > 0 ? 'aumentato' : 'diminuito';
      items.push({
        key: 'fatturato',
        tone,
        title: `Fatturato ${verb} del ${Math.abs(dPct).toFixed(0)}% rispetto a ${MONTH_FULL[prev.month]}`,
        text: `Sei passata da ${fmt(prev.fatturato_totale)} a ${fmt(latest.fatturato_totale)} (${dAbs >= 0 ? '+' : ''}${fmt(dAbs)}). ${dPct >= 5 ? 'Mese in crescita.' : dPct <= -5 ? 'Verifica le cause: meno trattamenti, meno clienti nuovi o ticket più basso?' : 'Andamento stabile.'}`,
      });
    }

    // trattamenti — bound to "Trattamenti per mese"
    if (prev && prev.num_trattamenti > 0) {
      const dT = latest.num_trattamenti - prev.num_trattamenti;
      const dTPct = (dT / prev.num_trattamenti) * 100;
      const tone = dTPct >= 5 ? 'good' : dTPct <= -10 ? 'bad' : 'neutral';
      items.push({
        key: 'trattamenti',
        tone,
        title: `${latest.num_trattamenti} trattamenti (${dT >= 0 ? '+' : ''}${dT} vs ${MONTH_FULL[prev.month]})`,
        text: `${dTPct >= 5 ? 'Stai aumentando il volume — buon segno per la fidelizzazione.' : dTPct <= -10 ? 'Calo significativo: indaga se è dovuto a stagionalità, ferie del team o un cliente strategico perso.' : 'Volume in linea col trend.'} Ricavo medio per trattamento del periodo: ${fmtDec(avgRicavoPerTratt)}.`,
      });
    }

    // ricavo & margine — bound to "Margine per Trattamento"
    if (avgRicavoPerTratt && latest.ricavo_per_trattamento) {
      const diff = latest.ricavo_per_trattamento - avgRicavoPerTratt;
      const dPct = (diff / avgRicavoPerTratt) * 100;
      const tone = dPct >= 5 ? 'good' : dPct <= -5 ? 'bad' : 'neutral';
      items.push({
        key: 'ricavo',
        tone,
        title: `Ricavo per trattamento: ${fmtDec(latest.ricavo_per_trattamento)} (media periodo ${fmtDec(avgRicavoPerTratt)})`,
        text: dPct >= 5
          ? `Stai vendendo trattamenti a maggior valore aggiunto: continua a proporre il mix premium.`
          : dPct <= -5
          ? `Mese con scontrino medio inferiore alla media. Cause tipiche: troppe promozioni, mix sbilanciato sui trattamenti base, sconti applicati.`
          : `Scontrino medio in linea con la tua media — bravo nel mantenere prezzi stabili.`,
      });
    }
    if (latest.margine_per_trattamento != null && avgMarginePerTratt) {
      const diff = latest.margine_per_trattamento - avgMarginePerTratt;
      const dPct = avgMarginePerTratt !== 0 ? (diff / Math.abs(avgMarginePerTratt)) * 100 : 0;
      const tone = dPct >= 5 ? 'good' : dPct <= -10 ? 'bad' : 'neutral';
      items.push({
        key: 'margine',
        tone,
        title: `Margine per trattamento: ${fmtDec(latest.margine_per_trattamento)} (media ${fmtDec(avgMarginePerTratt)})`,
        text: dPct >= 5
          ? `Il margine è migliorato. Stai gestendo bene il rapporto ricavi/costi collaboratori.`
          : dPct <= -10
          ? `Margine compresso questo mese. Le cause tipiche sono: aumentate le ore collaboratore senza un aumento proporzionale dei trattamenti, oppure sconti che non hanno coperto il costo orario.`
          : `Margine stabile rispetto alla media periodo.`,
      });
    }

    // clienti — bound to "Acquisizione Clienti"
    const avgClientiNuovi = data.length > 0 ? data.reduce((s, d) => s + (d.clienti_nuovi || 0), 0) / data.length : 0;
    if (latest.clienti_nuovi != null) {
      const diff = latest.clienti_nuovi - avgClientiNuovi;
      const tone = diff >= 2 ? 'good' : diff <= -3 ? 'bad' : 'neutral';
      items.push({
        key: 'clienti',
        tone,
        title: `${latest.clienti_nuovi} nuovi clienti (media ${avgClientiNuovi.toFixed(0)}/mese)`,
        text: diff >= 2
          ? `Mese con buona acquisizione: il marketing/passaparola sta funzionando.`
          : diff <= -3
          ? `Pochi nuovi clienti rispetto alla media: rivedi la presenza online (Google Maps, Instagram), il referral program o le campagne attive.`
          : `Acquisizione stabile sulla media.`,
      });
    }

    // utilizzo — bound to "% Utilizzo Studio"
    if (latest.pct_utilizzo_studio != null) {
      const u = latest.pct_utilizzo_studio;
      const tone = u >= 60 ? 'good' : u < 40 ? 'bad' : 'neutral';
      items.push({
        key: 'utilizzo',
        tone,
        title: `Utilizzo studio: ${u.toFixed(0)}%`,
        text: u >= 60
          ? `Studio ben sfruttato. Sopra il 75% potrebbe valere la pena assumere un altro collaboratore o ampliare gli orari.`
          : u < 40
          ? `Studio sottoutilizzato: hai ${(latest.ore_apertura - latest.ore_collaboratori).toFixed(0)} ore di apertura "vuote" al mese. Valuta di ridurre ore di apertura o promuovere fasce orarie meno occupate.`
          : `Tasso di occupazione discreto. Il target ideale è 60-75%.`,
      });
    }

    // mix collab — bound to "Mix Titolare vs Collaboratori"
    if (latest.fatturato_totale > 0) {
      const pctNow = Math.round((latest.fatturato_collaboratori || 0) / latest.fatturato_totale * 100);
      const firstWith = data.find(d => d.fatturato_totale > 0);
      const pctStart = firstWith ? Math.round((firstWith.fatturato_collaboratori || 0) / firstWith.fatturato_totale * 100) : pctNow;
      const trend = pctNow - pctStart;
      const tone = trend >= 5 ? 'good' : trend <= -5 ? 'bad' : 'neutral';
      items.push({
        key: 'mix',
        tone,
        title: `Quota collaboratori: ${pctNow}% (inizio periodo: ${pctStart}%)`,
        text: trend >= 5
          ? `Stai delegando sempre di più: il business cresce senza dipendere solo da te.`
          : trend <= -5
          ? `La quota di fatturato dei collaboratori sta diminuendo: rischi di tornare "monocentrica" su di te.`
          : `Mix stabile. A regime, una quota collaboratori >50% rende il business meno dipendente dal titolare.`,
      });
    }

    return items;
  };

  const narrative = buildNarrative();
  const toneColors = {
    good: { bg: 'bg-emerald-50 border-emerald-200', accent: 'text-emerald-700', icon: '✅' },
    bad: { bg: 'bg-rose-50 border-rose-200', accent: 'text-rose-700', icon: '⚠️' },
    neutral: { bg: 'bg-slate-50 border-slate-200', accent: 'text-slate-700', icon: 'ℹ️' },
  };

  const findNarr = (key) => narrative?.find(n => n.key === key);
  const renderNarrCard = (n) => {
    if (!n) return null;
    const c = toneColors[n.tone];
    return (
      <div className={`rounded-lg p-3 border mt-3 ${c.bg}`} data-testid={`narr-inline-${n.key}`}>
        <div className={`text-xs font-bold ${c.accent} mb-1`}>{c.icon} {n.title}</div>
        <div className="text-[11px] text-slate-700 leading-relaxed">{n.text}</div>
      </div>
    );
  };

  const ChartHelp = ({ children }) => (
    <details className="mt-2" data-testid="chart-help">
      <summary className="text-[11px] text-blue-700 cursor-pointer hover:text-blue-900 font-medium">💡 Come si legge questo grafico</summary>
      <div className="mt-2 text-[11px] text-slate-700 leading-relaxed bg-slate-50 rounded p-2">{children}</div>
    </details>
  );

  return (
    <div className="space-y-6" data-testid="clinic-kpi-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Analisi Operativa Studio</h2>
          <p className="text-sm text-muted-foreground">
            KPI operativi: trattamenti, collaboratori, acquisizione clienti
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-32" data-testid="clinic-kpi-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleDownloadTemplate} data-testid="clinic-kpi-download-template-btn">
            <Download className="h-4 w-4 mr-2" />
            Scarica Template
          </Button>
          <label>
            <Button variant="outline" asChild disabled={uploading} data-testid="clinic-kpi-upload-btn">
              <span>
                {uploading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-2" />}
                {uploading ? "Importazione..." : "Importa Excel"}
              </span>
            </Button>
            <input type="file" accept=".xlsx,.xls" onChange={handleUpload} className="hidden" />
          </label>
        </div>
      </div>

      {/* Compact monthly headline */}
      {latest && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="kpi-monthly-headline">
          <Activity className="h-4 w-4 text-blue-700" />
          Lettura automatica di <strong className="text-slate-800">{MONTH_FULL[latest.month]} {latest.year}</strong> — le spiegazioni sono direttamente sotto ogni grafico.
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          title="Ultimo Mese"
          value={fmt(latest.fatturato_totale)}
          subtitle={`${MONTH_FULL[latest.month]} ${latest.year}`}
          delta={delta(latest, prev, 'fatturato_totale')}
          icon={DollarSign}
          testId="kpi-card-fatturato"
        />
        <SummaryCard
          title="Trattamenti"
          value={fmtNum(latest.num_trattamenti)}
          subtitle={`${MONTH_FULL[latest.month]} ${latest.year}`}
          delta={delta(latest, prev, 'num_trattamenti')}
          icon={Activity}
          testId="kpi-card-trattamenti"
        />
        <SummaryCard
          title="Ricavo/Trattamento"
          value={fmtDec(latest.ricavo_per_trattamento)}
          subtitle="Media periodo"
          secondaryValue={avgRicavoPerTratt ? fmtDec(avgRicavoPerTratt) : null}
          secondaryLabel="media"
          icon={TrendingUp}
          testId="kpi-card-ricavo-tratt"
        />
        <SummaryCard
          title="Margine/Trattamento"
          value={latest.margine_per_trattamento != null ? fmtDec(latest.margine_per_trattamento) : (latest.costo_per_trattamento != null ? fmtDec(latest.ricavo_per_trattamento - latest.costo_per_trattamento) : "—")}
          subtitle={latest.costo_per_trattamento != null ? `Costo ${fmtDec(latest.costo_per_trattamento)} / Ricavo ${fmtDec(latest.ricavo_per_trattamento)}` : "Dati costi non disponibili"}
          secondaryValue={avgMarginePerTratt ? fmtDec(avgMarginePerTratt) : null}
          secondaryLabel="media periodo"
          icon={TrendingUp}
          testId="kpi-card-margine-tratt"
          variant={latest.margine_per_trattamento != null && latest.margine_per_trattamento < (avgMarginePerTratt || 0) ? "danger" : "default"}
        />
      </div>

      {/* Revenue Trend Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Trend Fatturato</CardTitle>
          <CardDescription>Fatturato titolare vs collaboratori per mese</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
              <RechartsTooltip formatter={(v, name) => [fmt(v), name]} />
              <Legend />
              <Bar dataKey="fatturato_titolare" name="Fatturato Titolare" fill="#3b82f6" radius={[3,3,0,0]} stackId="fatt" />
              <Bar dataKey="fatturato_collaboratori" name="Fatturato Collaboratori" fill="#8b5cf6" radius={[3,3,0,0]} stackId="fatt" />
              <Line dataKey="num_trattamenti" name="Trattamenti" stroke="#f59e0b" yAxisId="right" strokeWidth={2} dot={{ r: 3 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
            </ComposedChart>
          </ResponsiveContainer>
          {renderNarrCard(findNarr('fatturato'))}
          {renderNarrCard(findNarr('trattamenti'))}
        </CardContent>
      </Card>

      {/* Cost per Treatment + Efficiency */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Margine per Trattamento</CardTitle>
            <CardDescription>Ricavo, costo collaboratori e margine per ogni trattamento</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}€`} />
                <RechartsTooltip formatter={(v, name) => [fmtDec(v), name]} />
                <Legend />
                <Bar dataKey="ricavo_per_trattamento" name="Ricavo/Tratt." fill="#3b82f6" radius={[3,3,0,0]} />
                <Bar dataKey="costo_per_trattamento" name="Costo Collab./Tratt." fill="#ef4444" radius={[3,3,0,0]} />
                <Line dataKey="margine_per_trattamento" name="Margine/Tratt." stroke="#10b981" strokeWidth={2.5} dot={{ r: 4, fill: '#10b981' }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
            {renderNarrCard(findNarr('ricavo'))}
            {renderNarrCard(findNarr('margine'))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">% Utilizzo Studio</CardTitle>
            <CardDescription>Ore collaboratori su ore di apertura</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} unit="%" domain={[0, 100]} />
                <RechartsTooltip formatter={(v) => [`${v}%`, '% Utilizzo']} />
                <Bar dataKey="pct_utilizzo_studio" name="% Utilizzo" fill="#8b5cf6" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
            {renderNarrCard(findNarr('utilizzo'))}
          </CardContent>
        </Card>
      </div>

      {/* Indicatori di Produttività — split in 2 simpler charts */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Trattamenti per Ora</CardTitle>
            <CardDescription>Quanti trattamenti vengono fatti in 1 ora di apertura e in 1 ora di collaboratore</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <RechartsTooltip formatter={(v, name) => [v?.toFixed(2), name]} />
                <Legend />
                <Line dataKey="tratt_per_ora_apertura" name="Tratt./Ora Apertura" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
                <Line dataKey="tratt_per_ora_collab" name="Tratt./Ora Collab." stroke="#ef4444" strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
            <div className="text-[11px] text-slate-600 mt-2 bg-slate-50 rounded p-2 leading-relaxed">
              <strong>Come si legge:</strong> più alta è la linea, più "denso" è il lavoro. La <span className="text-blue-600 font-semibold">linea blu</span> dice quanti trattamenti la struttura riesce a erogare per ogni ora di apertura. La <span className="text-red-600 font-semibold">linea rossa</span> misura la produttività dei singoli collaboratori (idealmente ≥ 1 trattamento/ora).
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Fatturato per Ora</CardTitle>
            <CardDescription>Quanto fatturato viene generato per ogni ora di apertura e di collaboratore</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}€`} />
                <RechartsTooltip formatter={(v, name) => [fmt(v), name]} />
                <Legend />
                <Line dataKey="fatt_per_ora_apertura" name="€/Ora Apertura" stroke="#10b981" strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
                <Line dataKey="fatt_per_ora_collab" name="€/Ora Collab." stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 4 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
            <div className="text-[11px] text-slate-600 mt-2 bg-slate-50 rounded p-2 leading-relaxed">
              <strong>Come si legge:</strong> è il numero che le banche guardano per capire la "densità" del business. La <span className="text-emerald-600 font-semibold">linea verde</span> è quanto la struttura genera per ogni ora aperta; <span className="text-amber-600 font-semibold">la gialla</span> dice quanto produce ogni ora di collaboratore — deve essere sempre superiore al costo orario del collaboratore.
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Client Acquisition */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Acquisizione Clienti</CardTitle>
          <CardDescription>Nuovi clienti e fonti di acquisizione</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <RechartsTooltip />
              <Legend />
              <Bar dataKey="fonte_google" name="Google" fill="#4285F4" stackId="fonti" radius={[0,0,0,0]} />
              <Bar dataKey="fonte_passaparola" name="Passaparola" fill="#34A853" stackId="fonti" />
              <Bar dataKey="fonte_vetrina" name="Vetrina" fill="#FBBC04" stackId="fonti" />
              <Bar dataKey="fonte_affiliati" name="Affiliati" fill="#EA4335" stackId="fonti" radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
          {renderNarrCard(findNarr('clienti'))}
        </CardContent>
      </Card>

      {/* Collaboratori mix trend */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Mix Fatturato: Titolare vs Collaboratori</CardTitle>
          <CardDescription>Evoluzione della quota di fatturato generata dai collaboratori</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} unit="%" domain={[0, 100]} />
              <RechartsTooltip formatter={(v) => [`${v}%`]} />
              <Area dataKey="pct_collab" name="% Collaboratori" fill="#8b5cf640" stroke="#8b5cf6" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
          {renderNarrCard(findNarr('mix'))}
        </CardContent>
      </Card>

      {/* ===== SEZIONE 1: CE per Linea di Business ===== */}
      <LineeBusinessSection data={data} chartData={chartData} />

      {/* ===== SEZIONE 2: Capacita e Saturazione ===== */}
      <CapacitaSaturazioneSection data={data} chartData={chartData} />

      {/* ===== SEZIONE 3: Break-Even Collaboratore ===== */}
      <BreakEvenCollabSection data={data} chartData={chartData} />

      {/* Detail Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Dettaglio Mensile</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 bg-white z-10">Mese</TableHead>
                  <TableHead className="text-right">Fatt. Titolare</TableHead>
                  <TableHead className="text-right">Fatt. Collab.</TableHead>
                  <TableHead className="text-right">Totale</TableHead>
                  <TableHead className="text-right">Trattam.</TableHead>
                  <TableHead className="text-right">Ore Collab.</TableHead>
                  <TableHead className="text-right">Ore Apertura</TableHead>
                  <TableHead className="text-right">Costi Collab.</TableHead>
                  <TableHead className="text-right">Costo/Tratt.</TableHead>
                  <TableHead className="text-right">Ricavo/Tratt.</TableHead>
                  <TableHead className="text-right">Margine/Tratt.</TableHead>
                  <TableHead className="text-right">Tratt./h Apert.</TableHead>
                  <TableHead className="text-right">Tratt./h Collab.</TableHead>
                  <TableHead className="text-right">Fatt./h Apert.</TableHead>
                  <TableHead className="text-right">Fatt./h Collab.</TableHead>
                  <TableHead className="text-right">% Utilizzo</TableHead>
                  <TableHead className="text-right">Ticket Medio</TableHead>
                  <TableHead className="text-right">Clienti Nuovi</TableHead>
                  <TableHead className="text-right">Conversioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((d, i) => (
                  <TableRow key={i} data-testid={`clinic-kpi-row-${d.year}-${d.month}`}>
                    <TableCell className="sticky left-0 bg-white z-10 font-medium whitespace-nowrap">
                      {MONTH_NAMES[d.month]} {d.year}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(d.fatturato_titolare)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(d.fatturato_collaboratori)}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{fmt(d.fatturato_totale)}</TableCell>
                    <TableCell className="text-right tabular-nums">{d.num_trattamenti}</TableCell>
                    <TableCell className="text-right tabular-nums">{d.ore_collaboratori}</TableCell>
                    <TableCell className="text-right tabular-nums">{d.ore_apertura}</TableCell>
                    <TableCell className="text-right tabular-nums">{d.costi_collaboratori ? fmt(d.costi_collaboratori) : "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {d.costo_per_trattamento != null ? (
                        <Badge variant="outline" className={d.costo_per_trattamento > 30 ? "border-red-300 text-red-700" : d.costo_per_trattamento > 15 ? "border-amber-300 text-amber-700" : "border-green-300 text-green-700"}>
                          {fmtDec(d.costo_per_trattamento)}
                        </Badge>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{fmtDec(d.ricavo_per_trattamento)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {d.margine_per_trattamento != null ? (
                        <Badge variant="outline" className={d.margine_per_trattamento < 50 ? "border-red-300 text-red-700 font-bold" : d.margine_per_trattamento < 70 ? "border-amber-300 text-amber-700" : "border-green-300 text-green-700"}>
                          {fmtDec(d.margine_per_trattamento)}
                        </Badge>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{d.tratt_per_ora_apertura ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{d.tratt_per_ora_collab ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{d.fatt_per_ora_apertura != null ? fmt(d.fatt_per_ora_apertura) : "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{d.fatt_per_ora_collab != null ? fmt(d.fatt_per_ora_collab) : "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {d.pct_utilizzo_studio != null ? (
                        <Badge variant="outline" className={d.pct_utilizzo_studio > 60 ? "border-green-300 text-green-700" : d.pct_utilizzo_studio > 30 ? "border-amber-300 text-amber-700" : "border-red-300 text-red-700"}>
                          {d.pct_utilizzo_studio}%
                        </Badge>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{fmtDec(d.ticket_medio)}</TableCell>
                    <TableCell className="text-right tabular-nums">{d.clienti_nuovi}</TableCell>
                    <TableCell className="text-right tabular-nums">{d.conversioni}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Summary Card sub-component
const SummaryCard = ({ title, value, subtitle, delta, secondaryValue, secondaryLabel, icon: Icon, testId, variant = "default" }) => {
  const isPositive = delta != null && delta >= 0;
  const DeltaIcon = isPositive ? TrendingUp : TrendingDown;

  return (
    <Card className={`overflow-hidden ${variant === "danger" ? "border-red-200" : ""}`} data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="p-2 rounded-lg bg-slate-100">
            <Icon className="h-4 w-4 text-slate-600" />
          </div>
          {delta != null && (
            <div className={`flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded-full ${
              isPositive ? "text-emerald-700 bg-emerald-50" : "text-red-700 bg-red-50"
            }`}>
              <DeltaIcon className="h-3 w-3" />
              {Math.abs(delta).toFixed(1)}%
            </div>
          )}
        </div>
        <div className="text-xl font-bold tabular-nums">{value}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>
        {secondaryValue && (
          <div className="text-[10px] text-muted-foreground mt-1">
            {secondaryLabel}: <span className="font-semibold">{secondaryValue}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


// ===== SEZIONE 1: CE per Linea di Business =====
const LineeBusinessSection = ({ data }) => {
  const MONTHS = ["","Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
  // Compute per-month line-of-business data
  const lob = data.map(d => {
    const oreTitolare = Math.max(0, (d.ore_apertura || 0) - (d.ore_collaboratori || 0));
    const fattTit = d.fatturato_titolare || 0;
    const fattCol = d.fatturato_collaboratori || 0;
    const costiCol = d.costi_collaboratori || 0;
    const profittoCollab = fattCol - costiCol;
    const marginePctCollab = fattCol > 0 ? Math.round(profittoCollab / fattCol * 100) : null;
    const fattPerOraTit = oreTitolare > 0 ? Math.round(fattTit / oreTitolare) : null;
    const fattPerOraCol = d.ore_collaboratori > 0 ? Math.round(fattCol / d.ore_collaboratori) : null;
    const profittoPerOraCol = d.ore_collaboratori > 0 ? Math.round(profittoCollab / d.ore_collaboratori) : null;
    // BEP: quanti trattamenti deve fare il collab per coprire il suo costo
    // ricavo medio per trattamento collab ≈ fatturato totale / trattamenti totali (approssimazione)
    const ricavoMedioTratt = d.num_trattamenti > 0 ? d.fatturato_totale / d.num_trattamenti : 0;
    const bepTrattCollab = ricavoMedioTratt > 0 && costiCol > 0 ? Math.ceil(costiCol / ricavoMedioTratt) : null;
    return {
      label: `${MONTHS[d.month]} ${String(d.year).slice(2)}`,
      fattTitolare: fattTit,
      fattCollab: fattCol,
      costiCollab: costiCol,
      profittoCollab,
      marginePctCollab,
      fattPerOraTit,
      fattPerOraCol,
      profittoPerOraCol,
      oreTitolare,
      oreCollab: d.ore_collaboratori || 0,
      bepTrattCollab,
    };
  });

  const fmt = (v) => v != null ? new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v) : "—";

  // Latest month with collab data
  const withCollab = lob.filter(d => d.costiCollab > 0);
  const latestC = withCollab.length > 0 ? withCollab[withCollab.length - 1] : null;

  return (
    <div className="space-y-4" data-testid="linee-business-section">
      <div className="flex items-center gap-2 pt-2">
        <div className="h-1 w-8 bg-blue-600 rounded" />
        <h3 className="text-lg font-bold">CE per Linea: Titolare vs Collaboratori</h3>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Fatt./Ora Titolare</div>
            <div className="text-lg font-bold">{latestC ? fmt(latestC.fattPerOraTit) : "—"}</div>
            <div className="text-[10px] text-muted-foreground">ultimo mese con dati</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Fatt./Ora Collaboratori</div>
            <div className="text-lg font-bold">{latestC ? fmt(latestC.fattPerOraCol) : "—"}</div>
            <div className="text-[10px] text-muted-foreground">ultimo mese con dati</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Profitto Netto Collab.</div>
            <div className={`text-lg font-bold ${latestC && latestC.profittoCollab < 0 ? "text-red-600" : "text-emerald-600"}`}>
              {latestC ? fmt(latestC.profittoCollab) : "—"}
            </div>
            <div className="text-[10px] text-muted-foreground">fatturato - costi collab.</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Margine % Collaboratori</div>
            <div className={`text-lg font-bold ${latestC && latestC.marginePctCollab < 30 ? "text-red-600" : latestC && latestC.marginePctCollab < 60 ? "text-amber-600" : "text-emerald-600"}`}>
              {latestC && latestC.marginePctCollab != null ? `${latestC.marginePctCollab}%` : "—"}
            </div>
            <div className="text-[10px] text-muted-foreground">(fatt. - costi) / fatt. collab.</div>
          </CardContent>
        </Card>
      </div>

      {/* Chart: Profitto titolare vs profitto collaboratori */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Profitto per Linea di Business</CardTitle>
          <CardDescription>Fatturato titolare (netto = lordo) vs Margine collaboratori (fatturato - costi)</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={lob}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
              <RechartsTooltip formatter={(v, name) => [fmt(v), name]} />
              <Legend />
              <Bar dataKey="fattTitolare" name="Fatturato Titolare" fill="#3b82f6" radius={[3,3,0,0]} stackId="profit" />
              <Bar dataKey="profittoCollab" name="Margine Collaboratori" fill="#10b981" radius={[3,3,0,0]} stackId="profit" />
              <Bar dataKey="costiCollab" name="Costo Collaboratori" fill="#ef444480" radius={[3,3,0,0]} stackId="profit" />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Chart: Resa oraria confronto */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Resa Oraria: Titolare vs Collaboratori</CardTitle>
            <CardDescription>Fatturato generato per ogni ora lavorata</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart data={lob}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}€`} />
                <RechartsTooltip formatter={(v, name) => [fmt(v), name]} />
                <Legend />
                <Line dataKey="fattPerOraTit" name="€/h Titolare" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                <Line dataKey="fattPerOraCol" name="€/h Collaboratori" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                <Line dataKey="profittoPerOraCol" name="Profitto/h Collab." stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">BEP Collaboratori</CardTitle>
            <CardDescription>Trattamenti minimi per coprire il costo collaboratori</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart data={lob}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="bepTrattCollab" name="BEP (trattamenti)" fill="#f59e0b" radius={[3,3,0,0]} />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};


// ===== SEZIONE 2: Capacità e Saturazione =====
const CapacitaSaturazioneSection = ({ data }) => {
  const MONTHS = ["","Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
  // Best trattamenti/h from historical data as benchmark
  const trattPerOraValues = data.filter(d => d.ore_apertura > 0).map(d => d.num_trattamenti / d.ore_apertura);
  const bestTrattPerOra = trattPerOraValues.length > 0 ? Math.max(...trattPerOraValues) : 1;
  // Use 75th percentile as "realistic capacity" instead of absolute max
  const sorted = [...trattPerOraValues].sort((a,b) => a-b);
  const p75 = sorted[Math.floor(sorted.length * 0.75)] || bestTrattPerOra;

  const cap = data.map(d => {
    const oreAp = d.ore_apertura || 0;
    const capacitaMax = Math.round(oreAp * p75);
    const saturazione = capacitaMax > 0 ? Math.round(d.num_trattamenti / capacitaMax * 100) : 0;
    const potenziale = Math.max(0, capacitaMax - d.num_trattamenti);
    const ricavoMedio = d.num_trattamenti > 0 ? d.fatturato_totale / d.num_trattamenti : 0;
    const fatturatoPerso = Math.round(potenziale * ricavoMedio);
    return {
      label: `${MONTHS[d.month]} ${String(d.year).slice(2)}`,
      trattamenti: d.num_trattamenti,
      capacitaMax,
      saturazione,
      potenziale,
      fatturatoPerso,
      oreApertura: oreAp,
    };
  });

  const fmt = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v || 0);
  const latest = cap[cap.length - 1];
  const avgSaturazione = cap.length > 0 ? Math.round(cap.reduce((s, d) => s + d.saturazione, 0) / cap.length) : 0;

  return (
    <div className="space-y-4" data-testid="capacita-saturazione-section">
      <div className="flex items-center gap-2 pt-2">
        <div className="h-1 w-8 bg-cyan-600 rounded" />
        <h3 className="text-lg font-bold">Capacita e Saturazione Studio</h3>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-l-4 border-l-cyan-500">
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Benchmark Tratt./h</div>
            <div className="text-lg font-bold">{p75.toFixed(2)}</div>
            <div className="text-[10px] text-muted-foreground">75° percentile storico</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-cyan-500">
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Saturazione Ultimo Mese</div>
            <div className={`text-lg font-bold ${latest?.saturazione > 80 ? "text-emerald-600" : latest?.saturazione > 50 ? "text-amber-600" : "text-red-600"}`}>
              {latest?.saturazione}%
            </div>
            <div className="text-[10px] text-muted-foreground">{latest?.trattamenti} su {latest?.capacitaMax} possibili</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-cyan-500">
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Saturazione Media</div>
            <div className={`text-lg font-bold ${avgSaturazione > 80 ? "text-emerald-600" : avgSaturazione > 50 ? "text-amber-600" : "text-red-600"}`}>
              {avgSaturazione}%
            </div>
            <div className="text-[10px] text-muted-foreground">su tutto il periodo</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-400">
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Fatturato Potenziale Perso</div>
            <div className="text-lg font-bold text-red-600">{latest ? fmt(latest.fatturatoPerso) : "—"}</div>
            <div className="text-[10px] text-muted-foreground">ultimo mese, trattamenti mancanti</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Trattamenti Effettivi vs Capacita Massima</CardTitle>
          <CardDescription>Capacita calcolata su {p75.toFixed(2)} trattamenti/h (75° percentile). L'area rossa e il potenziale inespresso.</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={cap}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} unit="%" />
              <RechartsTooltip />
              <Legend />
              <Bar dataKey="trattamenti" name="Trattamenti Effettivi" fill="#3b82f6" radius={[3,3,0,0]} />
              <Bar dataKey="potenziale" name="Potenziale Inespresso" fill="#ef444440" radius={[3,3,0,0]} />
              <Line dataKey="saturazione" name="% Saturazione" stroke="#f59e0b" yAxisId="right" strokeWidth={2.5} dot={{ r: 4, fill: '#f59e0b' }} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};


// ===== SEZIONE 3: Break-Even Collaboratore =====
const BreakEvenCollabSection = ({ data }) => {
  const MONTHS = ["","Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
  const fmt = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v || 0);

  // Filter months with collaborator data
  const withCollab = data.filter(d => d.ore_collaboratori > 0 && d.costi_collaboratori > 0);
  if (withCollab.length === 0) return null;

  // Averages
  const avgCostoOrario = withCollab.reduce((s, d) => s + d.costi_collaboratori / d.ore_collaboratori, 0) / withCollab.length;
  const avgRicavoTratt = data.reduce((s, d) => s + (d.num_trattamenti > 0 ? d.fatturato_totale / d.num_trattamenti : 0), 0) / data.length;
  // Average treatments per collaborator hour
  const avgTrattPerOraCol = withCollab.reduce((s, d) => s + d.num_trattamenti / d.ore_collaboratori, 0) / withCollab.length;

  // BEP: quante ore/mese deve lavorare un collaboratore per coprire il suo costo
  // Se fattura avgTrattPerOraCol * avgRicavoTratt per ora, a quante ore = costo?
  const fattPerOraCollab = avgTrattPerOraCol * avgRicavoTratt;
  const bepOreGiorno = avgCostoOrario > 0 && fattPerOraCollab > avgCostoOrario ? 1 : null;

  const bepData = withCollab.map(d => {
    const costoOrario = d.ore_collaboratori > 0 ? d.costi_collaboratori / d.ore_collaboratori : 0;
    const fattOraCol = d.ore_collaboratori > 0 ? d.fatturato_collaboratori / d.ore_collaboratori : 0;
    const margineOraCol = fattOraCol - costoOrario;
    const roiCollab = d.costi_collaboratori > 0 ? Math.round((d.fatturato_collaboratori - d.costi_collaboratori) / d.costi_collaboratori * 100) : null;
    // Soglia allarme: fatturato collab < 1.5x costi collab
    const inAllarme = d.fatturato_collaboratori < d.costi_collaboratori * 1.5;
    return {
      label: `${MONTHS[d.month]} ${String(d.year).slice(2)}`,
      costoOrario: Math.round(costoOrario * 100) / 100,
      fattOraCol: Math.round(fattOraCol),
      margineOraCol: Math.round(margineOraCol),
      roiCollab,
      fattCollab: d.fatturato_collaboratori,
      costiCollab: d.costi_collaboratori,
      inAllarme,
    };
  });

  const latestBep = bepData[bepData.length - 1];
  const avgROI = bepData.filter(d => d.roiCollab != null).reduce((s, d) => s + d.roiCollab, 0) / bepData.filter(d => d.roiCollab != null).length;

  return (
    <div className="space-y-4" data-testid="break-even-collab-section">
      <div className="flex items-center gap-2 pt-2">
        <div className="h-1 w-8 bg-amber-500 rounded" />
        <h3 className="text-lg font-bold">Break-Even Collaboratori</h3>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Costo Orario Medio</div>
            <div className="text-lg font-bold">{fmt(avgCostoOrario)}/h</div>
            <div className="text-[10px] text-muted-foreground">costi collab. / ore lavorate</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Fatturato/Ora Collab.</div>
            <div className="text-lg font-bold">{fmt(fattPerOraCollab)}/h</div>
            <div className="text-[10px] text-muted-foreground">quanto produce ogni ora</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">ROI Collaboratori</div>
            <div className={`text-lg font-bold ${latestBep?.roiCollab < 50 ? "text-red-600" : latestBep?.roiCollab < 100 ? "text-amber-600" : "text-emerald-600"}`}>
              {latestBep?.roiCollab != null ? `${latestBep.roiCollab}%` : "—"}
            </div>
            <div className="text-[10px] text-muted-foreground">ultimo mese (media: {Math.round(avgROI)}%)</div>
          </CardContent>
        </Card>
        <Card className={`border-l-4 ${latestBep?.inAllarme ? "border-l-red-500 bg-red-50/50" : "border-l-emerald-500"}`}>
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Stato</div>
            <div className={`text-lg font-bold ${latestBep?.inAllarme ? "text-red-600" : "text-emerald-600"}`}>
              {latestBep?.inAllarme ? "SOTTO SOGLIA" : "OK"}
            </div>
            <div className="text-[10px] text-muted-foreground">allarme se fatturato &lt; 1.5x costi</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">ROI Collaboratori e Soglia di Allarme</CardTitle>
          <CardDescription>Se il ROI scende sotto 50%, il collaboratore sta generando margine insufficiente. Sotto 0% sta costando piu di quanto produce.</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={bepData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}€`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} unit="%" />
              <RechartsTooltip formatter={(v, name) => [name.includes('ROI') ? `${v}%` : fmt(v), name]} />
              <Legend />
              <Bar dataKey="fattCollab" name="Fatturato Collab." fill="#3b82f6" radius={[3,3,0,0]} />
              <Bar dataKey="costiCollab" name="Costi Collab." fill="#ef4444" radius={[3,3,0,0]} />
              <Line dataKey="roiCollab" name="ROI Collab. (%)" stroke="#f59e0b" yAxisId="right" strokeWidth={2.5} dot={{ r: 4, fill: '#f59e0b' }} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Margine orario */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Costo vs Fatturato per Ora Collaboratore</CardTitle>
          <CardDescription>L'area verde e il margine netto per ogni ora lavorata dai collaboratori</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={bepData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}€`} />
              <RechartsTooltip formatter={(v, name) => [fmt(v), name]} />
              <Legend />
              <Area dataKey="fattOraCol" name="Fatturato/h" fill="#3b82f640" stroke="#3b82f6" strokeWidth={2} />
              <Area dataKey="costoOrario" name="Costo/h" fill="#ef444440" stroke="#ef4444" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};
